/**
 * 매뉴얼 id·이미지 파일 이름 거르기와 읽기 문턱 — 서버(API route)와 브라우저(가이드 화면)가 함께 쓴다.
 *
 * 🚨 요청에서 온 id·파일 이름은 **목록과 좁은 패턴으로만** 통과시킨다 — 경로 조각(`..`·`/`·`\`)이 섞인
 *   값이 파일 경로에 닿으면 `artifacts/` 밖을 읽게 된다.
 */

import type { PlanId } from 'src/types/plan/plan-domain';
import type { ManualId, ManualEntry } from 'src/types/manual/manual-domain';

import { passesMinPlan, type PlanViewer } from 'src/utils/plan/plan-tier';

import { MANUAL_CATALOG, GUIDE_MIN_PLAN, MANUAL_IMAGE_TYPES } from 'src/constants/manual-catalog';

/** 목록에 실린 id 인가 */
export function isManualId(value: unknown): value is ManualId {
  return typeof value === 'string' && MANUAL_CATALOG.some((entry) => entry.id === value);
}

/** 목록의 한 줄. 없으면 `null` */
export function findManual(id: unknown): ManualEntry | null {
  return MANUAL_CATALOG.find((entry) => entry.id === id) ?? null;
}

/**
 * 그림·영상 파일 이름 하나 — 영문 소문자·숫자·`-`·`_` 로 시작하고 끝이 아는 확장자다.
 * 영상의 첫 장면 그림(`<이름>.poster.png`)만 가운데 `.poster` 한 마디를 받는다.
 * 폴더·점 두 개·대문자 확장자는 받지 않는다.
 */
export function isManualImageFile(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 120) return false;
  const match = /^[a-z0-9][a-z0-9_-]*(?:\.poster)?\.([a-z0-9]+)$/.exec(value);
  return Boolean(match && MANUAL_IMAGE_TYPES[match[1]]);
}

/** 그림·영상 파일 이름 → Content-Type. 거른 이름에만 부른다 — 모르는 확장자면 `null` */
export function manualImageContentType(file: string): string | null {
  const ext = file.slice(file.lastIndexOf('.') + 1);
  return MANUAL_IMAGE_TYPES[ext] ?? null;
}

/** md 의 그림 주소가 무엇을 가리키나 — `.mp4`·`.webm` 은 영상, 그 밖은 그림 */
export function manualMediaKind(src: string | null | undefined): 'video' | 'image' {
  return /\.(?:mp4|webm)$/i.test(String(src ?? '')) ? 'video' : 'image';
}

/**
 * 영상 주소 → 그 영상의 첫 장면 그림 주소(`img/<id>/<이름>.mp4` → `img/<id>/<이름>.poster.png`).
 * 영상이 아니면 `null`.
 */
export function manualPosterSrc(src: string | null | undefined): string | null {
  const value = String(src ?? '');
  return manualMediaKind(value) === 'video' ? value.replace(/\.(?:mp4|webm)$/i, '.poster.png') : null;
}

/** 매뉴얼마다 본문 제목을 주는 API 주소(가이드 검색) */
export const MANUAL_HEADINGS_API_PATH = '/api/manual/headings';

/** 매뉴얼 본문을 주는 API 주소 */
export function manualDocApiPath(id: ManualId): string {
  return `/api/manual/${id}`;
}

/**
 * md 안의 이미지 주소(`img/<id>/<파일>`) → 그 이미지를 주는 API 주소.
 * 모양이 다르거나 목록 밖 id·거르지 못한 파일 이름이면 `null` — 그 그림은 그리지 않는다.
 */
export function manualImageApiPath(src: string | null | undefined): string | null {
  if (!src) return null;
  const match = /^(?:\.\/)?img\/([^/]+)\/([^/]+)$/.exec(src);
  if (!match) return null;
  const [, id, file] = match;
  if (!isManualId(id) || !isManualImageFile(file)) return null;
  return `/api/manual/${id}/img/${file}`;
}

/**
 * 이 매뉴얼의 읽기 문턱 — 매뉴얼에 제 문턱이 있으면 그것, 없으면 `GUIDE_MIN_PLAN`.
 * 목록 밖 id 는 공통 문턱을 준다(그 요청은 route 가 404 로 끊는다).
 */
export function manualMinPlan(id: unknown): PlanId | null {
  return findManual(id)?.minPlan ?? GUIDE_MIN_PLAN;
}

/** 이 사람이 이 매뉴얼을 읽을 수 있나 — `id` 를 빼면 가이드 홈(공통 문턱) */
export function canReadManual(viewer: PlanViewer, id?: ManualId): boolean {
  return passesMinPlan(viewer, id ? manualMinPlan(id) : GUIDE_MIN_PLAN);
}
